import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, ImageIcon, Copy, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import api from "@/lib/api";

// -------------------------------------------------------------------
// Homepage Editor (singleton `/api/settings` + PUT `/api/admin/settings`)
// -------------------------------------------------------------------

export function HomepageEditor() {
  const [form, setForm] = useState({
    hero_subtitle: "",
    cta_primary: "Apply for Franchise",
    cta_secondary: "View Products",
    stats: [],
    instagram_url: "",
    facebook_url: "",
    youtube_url: "",
    linkedin_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        setForm({
          hero_subtitle: data.hero_subtitle || "",
          cta_primary: data.cta_primary || "Apply for Franchise",
          cta_secondary: data.cta_secondary || "View Products",
          stats: Array.isArray(data.stats) ? data.stats : [],
          instagram_url: data.instagram_url || "",
          facebook_url: data.facebook_url || "",
          youtube_url: data.youtube_url || "",
          linkedin_url: data.linkedin_url || "",
        });
      })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const setStat = (i, key, value) =>
    setForm((f) => ({
      ...f,
      stats: f.stats.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)),
    }));

  const addStat = () =>
    setForm((f) => ({ ...f, stats: [...f.stats, { value: "", label: "" }] }));

  const removeStat = (i) =>
    setForm((f) => ({ ...f, stats: f.stats.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Homepage settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-[#6b6a65]">Loading settings…</div>;

  const socialFields = [
    { key: "instagram_url", label: "Instagram URL", Icon: Instagram, placeholder: "https://instagram.com/saanswellness" },
    { key: "facebook_url", label: "Facebook URL", Icon: Facebook, placeholder: "https://facebook.com/saanswellness" },
    { key: "youtube_url", label: "YouTube URL", Icon: Youtube, placeholder: "https://youtube.com/@saanswellness" },
    { key: "linkedin_url", label: "LinkedIn URL", Icon: Linkedin, placeholder: "https://linkedin.com/company/saanswellness" },
  ];

  return (
    <div className="space-y-6" data-testid="homepage-editor">
      <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow">
        <div className="eyebrow text-[#d46a1d]">Hero &amp; CTA</div>
        <h3 className="mt-1 font-serif text-2xl text-[#202124]">Homepage copy</h3>
        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#4a4a48]">Hero subtitle</label>
            <textarea
              rows={2}
              className="form-field resize-none"
              value={form.hero_subtitle}
              placeholder="Breathe Better. Live Better."
              onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })}
              data-testid="homepage-hero-subtitle"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-[#4a4a48]">Primary CTA</label>
              <input
                className="form-field"
                value={form.cta_primary}
                onChange={(e) => setForm({ ...form, cta_primary: e.target.value })}
                data-testid="homepage-cta-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[#4a4a48]">Secondary CTA</label>
              <input
                className="form-field"
                value={form.cta_secondary}
                onChange={(e) => setForm({ ...form, cta_secondary: e.target.value })}
                data-testid="homepage-cta-secondary"
              />
            </div>
          </div>
          <button
            onClick={save}
            disabled={busy}
            data-testid="homepage-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#d46a1d] px-5 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
          >
            <Save size={15} /> Save changes
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow text-[#d46a1d]">Trust metrics</div>
            <h3 className="mt-1 font-serif text-2xl text-[#202124]">Homepage stats</h3>
          </div>
          <button
            onClick={addStat}
            data-testid="homepage-add-stat"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#d8cfc1] px-3 py-1.5 text-xs font-medium hover:border-[#d46a1d] hover:text-[#d46a1d]"
          >
            <Plus size={13} /> Add
          </button>
        </div>
        <div className="mt-4 space-y-3">
          {form.stats.length === 0 && (
            <div className="rounded-xl border border-dashed border-[#d8cfc1] p-6 text-center text-sm text-[#6b6a65]">No stats yet — add one.</div>
          )}
          {form.stats.map((s, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`homepage-stat-${i}`}>
              <input
                className="form-field flex-[0_0_120px]"
                value={s.value}
                placeholder="15+"
                onChange={(e) => setStat(i, "value", e.target.value)}
                data-testid={`homepage-stat-value-${i}`}
              />
              <input
                className="form-field flex-1"
                value={s.label}
                placeholder="Years of expertise"
                onChange={(e) => setStat(i, "label", e.target.value)}
                data-testid={`homepage-stat-label-${i}`}
              />
              <button
                onClick={() => removeStat(i)}
                data-testid={`homepage-stat-remove-${i}`}
                className="text-[#a8a498] hover:text-[#d46a1d]"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* Social Links */}
      <div className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow text-[#d46a1d]">Social Media</div>
            <h3 className="mt-1 font-serif text-2xl text-[#202124]">Public profile links</h3>
            <p className="text-sm text-[#6b6a65]">Only filled URLs show as icons in the footer. Leave blank to hide.</p>
          </div>
          <button
            onClick={save}
            disabled={busy}
            data-testid="homepage-social-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#d46a1d] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
          >
            <Save size={14} /> Save
          </button>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {socialFields.map(({ key, label, Icon, placeholder }) => (
            <div key={key}>
              <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-[#4a4a48]">
                <Icon size={14} className="text-[#1f5d3a]" /> {label}
              </label>
              <input
                className="form-field"
                value={form[key]}
                placeholder={placeholder}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`homepage-${key.replace("_url", "")}-input`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Activity Log (`/api/admin/audit`)
// -------------------------------------------------------------------

export function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/audit")
      .then(({ data }) => setLogs(data.logs || []))
      .catch(() => toast.error("Failed to load audit"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#6b6a65]">Loading activity…</div>;

  return (
    <div className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow" data-testid="activity-log">
      <h3 className="font-serif text-2xl text-[#202124]">Activity Log</h3>
      <p className="mt-1 text-sm text-[#6b6a65]">Recent admin actions ({logs.length})</p>
      <div className="mt-5 divide-y divide-[#f0ebe3]">
        {logs.length === 0 && (
          <div className="py-10 text-center text-sm text-[#6b6a65]">No activity yet.</div>
        )}
        {logs.map((l) => (
          <div key={l.id} className="grid grid-cols-12 gap-3 py-3 text-sm" data-testid={`log-${l.id}`}>
            <div className="col-span-3 text-[#6b6a65]">{new Date(l.created_at).toLocaleString()}</div>
            <div className="col-span-2 font-semibold uppercase text-[#1f5d3a]">{l.action}</div>
            <div className="col-span-2 text-[#4a4a48]">{l.entity}</div>
            <div className="col-span-3 truncate text-[#8a8880]">{l.entity_id}</div>
            <div className="col-span-2 text-right text-[#8a8880]">{l.actor}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Media Library (upload + browse + copy URL)
// -------------------------------------------------------------------

export function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/media");
      setItems(data.media || []);
    } catch {
      toast.error("Failed to load media");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.post("/admin/media", {
        filename: file.name,
        content_type: file.type || "image/png",
        data_base64: dataUrl,
      });
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div data-testid="media-library">
      <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h3 className="font-serif text-2xl text-[#202124]">Media Library</h3>
          <p className="text-sm text-[#6b6a65]">Upload images once, reuse everywhere ({items.length})</p>
        </div>
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#d46a1d] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] ${busy ? "opacity-60" : ""}`}
          data-testid="media-upload-btn"
        >
          <Upload size={15} />
          {busy ? "Uploading…" : "Upload image"}
          <input
            type="file"
            className="hidden"
            accept="image/*"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#d8cfc1] bg-white p-10 text-center text-[#6b6a65]" data-testid="media-empty">
          <ImageIcon size={30} className="mx-auto mb-2 text-[#c9c1b3]" />
          Upload your first image or product photo to get started.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {items.map((m) => (
            <div key={m.id} className="group overflow-hidden rounded-xl border border-[#e7e0d6] bg-white card-shadow" data-testid={`media-${m.id}`}>
              <img src={m.url} alt={m.filename} className="aspect-square w-full object-cover" />
              <div className="flex items-center justify-between gap-2 px-2 py-1.5 text-[11px] text-[#6b6a65]">
                <span className="truncate">{m.filename}</span>
                <button
                  onClick={() => copyUrl(m.url)}
                  data-testid={`media-copy-${m.id}`}
                  className="text-[#8a8880] hover:text-[#d46a1d]"
                  title="Copy URL"
                >
                  <Copy size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
